import { AttachIcon } from '@synergy/theme';
import { Box, Divider, IconButton, Link, Stack, Tooltip, styled } from '@mui/material';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Fragment, ReactElement } from 'react';
import { Media, UploadState } from '@synergy/models';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import urljoin from 'url-join';

import { PoolIconButton } from '../buttons';
import { PoolLineClampTypography } from '../pool-line-clamp-typography/pool-line-clamp-typography';
import { PoolLinearProgress } from '../pool-linear-progress/pool-linear-progress';
import { PoolTypography } from '../pool-typography/pool-typography';

const StyledListItem = styled('li')(({ theme }) => ({
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    justifyContent: 'start',
    gap: theme.spacing(1),
    minHeight: theme.spacing(7.5),
}));

const FileList = styled(Box)(({ theme }) => ({
    ...theme.typography.body2,
    padding: 0,
    margin: 0,
    listStyle: 'none',
    fontSize: 15,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
})) as typeof Box;

export interface PoolFileListProps {
    downloadBaseUrl?: string;
    acceptedFiles?: Media[];
    onDeleteClicked?: (file: Media) => void;
    onCancelClicked?: (file: Media) => void;
    /** Offers a pencil that names the file; the list shows the name instead. */
    onEditClicked?: (file: Media) => void;
    readOnly?: boolean;
    showCreateDate?: boolean;
    target?: string;
    hideTitle?: boolean;
    directDownload?: boolean;
    /**
     * "rail": for a narrow side rail. The name takes the full width over up to
     * three lines, date and type go underneath with small actions (R-266).
     */
    variant?: 'default' | 'rail';
}

/** "PDF" for "Angebot.pdf"; nothing for a name without an extension. */
const extensionOf = (media: Media): string => /\.([^.]+)$/.exec(media.originalName ?? '')?.[1]?.toUpperCase() ?? '';

const RAIL_ACTION_SX = { padding: '2px', color: 'grey.600', '& svg': { fontSize: 16 } } as const;

export const PoolFileList = (props: PoolFileListProps): ReactElement | null => {
    const { acceptedFiles, downloadBaseUrl, target, hideTitle, directDownload } = props;
    const rail = 'rail' === props.variant;

    const handleDeleteClicked = (media: Media) => () => {
        if (props.onDeleteClicked) {
            props.onDeleteClicked(media);
        }
    };

    const handleCancelClicked = (media: Media) => () => {
        if (props.onCancelClicked) {
            props.onCancelClicked(media);
        }
    };

    if (!acceptedFiles || acceptedFiles.length === 0) {
        return null;
    }

    /** A title says what the file is; the file name is what it was saved as. */
    const shownName = (media: Media): string => media.title || media.originalName;

    /** Only a title that says more than the file name without its extension. */
    const named = (media: Media): boolean =>
        Boolean(media.title) &&
        ![media.originalName, media.originalName?.replace(/\.[^.]+$/, '')].includes(media.title ?? '');

    const renderFileDetail = (media: Media): ReactElement | null => {
        const state = props.readOnly ? UploadState.Finished : media.state;

        switch (state) {
            case UploadState.InProgress: {
                return (
                    <>
                        <PoolLinearProgress variant="determinate" value={media.progress || 0} sx={{ ml: 1, mr: 3 }} />
                        {props.onCancelClicked && (
                            <PoolIconButton
                                color="primary"
                                onClick={handleCancelClicked(media)}
                                sx={{ transform: 'rotate(45deg)', marginLeft: 'auto', minWidth: 38 }}
                            >
                                <AddIcon />
                            </PoolIconButton>
                        )}
                    </>
                );
            }

            case UploadState.Finished: {
                if (rail) {
                    return renderRailFile(media);
                }

                return (
                    <>
                        <AttachIcon sx={{ mR: 1, fontSize: 18 }} />
                        {downloadBaseUrl && media.id >= 0 ? (
                            <Link
                                href={urljoin(downloadBaseUrl, media.pathname)}
                                download={directDownload ?? true ? media.originalName : undefined}
                                target={target || '_blank'}
                                sx={{
                                    color: theme => theme.palette.text.primary,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textDecoration: 'none',
                                    '&:hover': { color: theme => theme.palette.primary.main },
                                }}
                            >
                                <PoolLineClampTypography maxLines={2} title={shownName(media)}>
                                    {shownName(media)}
                                </PoolLineClampTypography>
                                <PoolLineClampTypography variant="body2" color="grey.500" title={media.originalName}>
                                    {/* Named, the file name would be gone from sight; it stays
                                        beside the date so the file can still be told apart. */}
                                    {named(media) && <>{media.originalName} · </>}
                                    <FormattedDate value={media.createdAt} dateStyle="medium" timeStyle="medium" />
                                </PoolLineClampTypography>
                            </Link>
                        ) : (
                            <PoolLineClampTypography>{shownName(media)}</PoolLineClampTypography>
                        )}
                        {!props.readOnly && props.onEditClicked && media.id >= 0 && (
                            <PoolIconButton
                                color="primary"
                                aria-label="Titel bearbeiten"
                                onClick={() => props.onEditClicked?.(media)}
                                sx={{ marginLeft: 'auto', minWidth: 38, '& svg': { fontSize: 18 } }}
                            >
                                <EditOutlinedIcon />
                            </PoolIconButton>
                        )}
                        {!props.readOnly && props.onDeleteClicked && (
                            <PoolIconButton
                                color="primary"
                                onClick={handleDeleteClicked(media)}
                                sx={{
                                    transform: 'rotate(45deg)',
                                    marginLeft: props.onEditClicked ? 0 : 'auto',
                                    minWidth: 38,
                                }}
                            >
                                <AddIcon />
                            </PoolIconButton>
                        )}
                    </>
                );
            }

            case UploadState.Failed: {
                return (
                    <>
                        <PoolLineClampTypography variant="body2" color="error">
                            {media.originalName} <FormattedMessage id="components.fileList.upload.failed" />
                        </PoolLineClampTypography>
                        <PoolIconButton color="secondary" onClick={handleDeleteClicked(media)} sx={{ minWidth: 38 }}>
                            <DeleteForeverIcon />
                        </PoolIconButton>
                    </>
                );
            }

            default:
                return null;
        }
    };

    const renderRailFile = (media: Media): ReactElement => (
        <Box sx={{ width: '100%', minWidth: 0, py: '6px' }}>
            <Tooltip
                placement="left"
                title={
                    <>
                        {media.originalName}
                        <br />
                        <FormattedDate value={media.createdAt} dateStyle="medium" timeStyle="short" />
                        {media.size ? ` · ${Math.max(1, Math.round(media.size / 1024))} KB` : ''}
                    </>
                }
            >
                <Box sx={{ display: 'flex', gap: '4px', fontSize: 13, lineHeight: 1.35 }}>
                    <AttachIcon sx={{ fontSize: 15, mt: '2px', flexShrink: 0 }} />
                    {downloadBaseUrl && media.id >= 0 ? (
                        <Link
                            href={urljoin(downloadBaseUrl, media.pathname)}
                            download={directDownload ?? true ? media.originalName : undefined}
                            target={target || '_blank'}
                            sx={{
                                minWidth: 0,
                                color: theme => theme.palette.text.primary,
                                textDecoration: 'none',
                                '&:hover': { color: theme => theme.palette.primary.main },
                            }}
                        >
                            <PoolLineClampTypography maxLines={3} sx={{ fontSize: 'inherit', wordBreak: 'break-word' }}>
                                {shownName(media)}
                            </PoolLineClampTypography>
                        </Link>
                    ) : (
                        <PoolLineClampTypography maxLines={3} sx={{ fontSize: 'inherit', wordBreak: 'break-word' }}>
                            {shownName(media)}
                        </PoolLineClampTypography>
                    )}
                </Box>
            </Tooltip>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    mt: '2px',
                    pl: '19px',
                    fontSize: 11.5,
                    color: 'grey.600',
                }}
            >
                <FormattedDate value={media.createdAt} dateStyle="medium" />
                {extensionOf(media) && <span>· {extensionOf(media)}</span>}
                <Box sx={{ flex: 1 }} />
                {!props.readOnly && props.onEditClicked && media.id >= 0 && (
                    <IconButton
                        size="small"
                        aria-label="Titel bearbeiten"
                        onClick={() => props.onEditClicked?.(media)}
                        sx={RAIL_ACTION_SX}
                    >
                        <EditOutlinedIcon />
                    </IconButton>
                )}
                {!props.readOnly && props.onDeleteClicked && (
                    <IconButton
                        size="small"
                        aria-label="Datei entfernen"
                        onClick={handleDeleteClicked(media)}
                        sx={RAIL_ACTION_SX}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );

    return acceptedFiles && acceptedFiles.length > 0 ? (
        <Stack gap={1.5}>
            {!hideTitle && (
                <PoolTypography
                    variant="caption"
                    sx={{ color: theme => theme.palette.grey[500], textAlign: 'left' }}
                    translationId="components.fileList.attachedFiles"
                />
            )}
            <FileList component="ul" sx={rail ? { border: 0, backgroundColor: 'transparent' } : undefined}>
                {acceptedFiles.map(file => {
                    return (
                        <Fragment key={`${file.pathname}_${file.size}`}>
                            <StyledListItem sx={rail ? { minHeight: 'auto', mx: 0 } : undefined}>
                                {renderFileDetail(file)}
                            </StyledListItem>
                            <Divider
                                sx={{
                                    borderColor: theme => theme.palette.grey[200],
                                    '&:last-child': {
                                        display: 'none',
                                    },
                                }}
                            />
                        </Fragment>
                    );
                })}
            </FileList>
        </Stack>
    ) : null;
};
